import React, { useState, useRef, useCallback, useEffect } from 'react';
import { TextPage } from '../TextPage/TextPage';
import { DropDownSelector, ButtonBasic, Checkbox } from '@lk-gtcom/ecomlab-components';
import './TimeGridSelector.scss';
const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const hours = Array.from({ length: 24 }, (_, i) => i); // 0-23


const TimeGridSelector = ({ selectedRange, setSelectedRange, child, interval = 15 }) => {
    const [selectedDays, setSelectedDays] = useState([]);
    // Динамическое создание option_prop на основе interval
    const getAvailableOptions = useCallback(() => {
        const allOptions = [
            { label: '15', value: '15' },
            { label: '30', value: '30' },
            { label: '60', value: '60' }
        ];
        // Фильтруем опции: оставляем только те, которые равны или кратны interval
        return allOptions.filter(opt => Number(opt.value) >= interval && Number(opt.value) % interval === 0);
    }, [interval]);
    
    const [selectedMinuteStep, setSelectedMinuteStep] = useState(getAvailableOptions()[0]); // Выбранный интервал минут
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [isCustom, setIsCustom] = useState(false)
    const isDragging = useRef(false);
    const dragAddMode = useRef(true);

    // Всегда показываем сетку с шагом interval из пропсов
    const minuteSteps = useCallback(() => {
        const steps = [];
        for (let minute = 0; minute < 60; minute += interval) {
            steps.push(minute);
        }
        return steps;
    }, [interval]);

    // Форматирование для API (дни 1-7)
    const formatForApi = useCallback((slots) => {
        return slots.map(slot => ({
            ...slot,
            weekday: slot.weekday + 1 // Конвертируем из 0-6 в 1-7
        }));
    }, []);

    // Переключение дня недели
    const toggleDay = useCallback((dayIndex) => {
        setSelectedDays(prev =>
            prev.includes(dayIndex)
                ? prev.filter(d => d !== dayIndex)
                : [...prev, dayIndex]
        );
    }, []);

    // Переключение слота
    const toggleSlot = useCallback((weekday, hour, minute, add) => {
        setSelectedSlots(prev => {
            const slot = { weekday, hour, minute };
            if (add) {
                const exists = prev.some(s =>
                    s.weekday === weekday &&
                    s.hour === hour &&
                    s.minute === minute
                );
                return exists ? prev : [...prev, slot];
            } else {
                return prev.filter(s =>
                    !(s.weekday === weekday &&
                        s.hour === hour &&
                        s.minute === minute)
                );
            }
        });
    }, []);

    // Обработчики для drag-and-drop
    const handleMouseDown = useCallback((weekday, hour, minute) => {
        isDragging.current = true;
        const exists = selectedSlots.some(s =>
            s.weekday === weekday &&
            s.hour === hour &&
            s.minute === minute
        );
        dragAddMode.current = !exists;
        toggleSlot(weekday, hour, minute, dragAddMode.current);
    }, [selectedSlots, toggleSlot]);

    const handleMouseUp = useCallback(() => {
        isDragging.current = false;
    }, []);

    const handleMouseEnter = useCallback((weekday, hour, minute) => {
        if (!isDragging.current) return;
        toggleSlot(weekday, hour, minute, dragAddMode.current);
    }, [toggleSlot]);

    // Проверка выбран ли слот
    const isSlotSelected = (weekday, hour, minute) => {
        return selectedSlots.some(s =>
            s.weekday === weekday &&
            s.hour === hour &&
            s.minute === minute
        );
    };

    // Проверка, является ли слот активным (все слоты активны)
    const isSlotActive = useCallback(() => {
        return true;
    }, []);

    // Форматирование слота для отображения
    const formatSlot = (slot) => {
        return `${days[slot.weekday]} ${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`;
    };

    // Генерация слотов по выбранному интервалу
    const generateSlotsByInterval = useCallback(() => {
        if (selectedDays.length === 0) return [];

        const slots = [];
        const currentInterval = Number(selectedMinuteStep?.value);

        selectedDays.forEach(weekday => {
            hours.forEach(hour => {
                for (let minute = 0; minute < 60; minute += currentInterval) {
                    slots.push({ weekday, hour, minute });
                }
            });
        });

        return slots;
    }, [selectedDays, selectedMinuteStep]);

    // Заполнить все слоты для выбранных дней с интервалом
    const fillAllSlots = useCallback(() => {
        const newSlots = generateSlotsByInterval();
        setSelectedSlots(prev => {
            // Удаляем старые слоты для выбранных дней
            const filtered = prev.filter(s => !selectedDays.includes(s.weekday));
            // Добавляем новые слоты
            return [...filtered, ...newSlots];
        });
    }, [generateSlotsByInterval, selectedDays]);

    // Очистить все слоты для выбранных дней
    const clearAllSlots = useCallback(() => {
        setSelectedSlots([])
    }, [selectedDays]);

    // Инициализация из props
    const [init, setInit] = useState(true);
    useEffect(() => {
        if (init && selectedRange.length > 0) {
            setSelectedSlots(selectedRange?.map((el) => ({ ...el, weekday: el?.weekday - 1 })));
            setSelectedDays([...new Set(selectedRange.map(el => el.weekday - 1))]);
            setInit(false);
        }
    }, [selectedRange]);

    useEffect(() => {
        if (setSelectedRange) {
            setSelectedRange(formatForApi(selectedSlots));
        }
    }, [selectedSlots, setSelectedRange, formatForApi]);

    // Обновляем selectedMinuteStep при изменении interval
    useEffect(() => {
        const availableOptions = getAvailableOptions();
        if (availableOptions.length > 0 && !availableOptions.some(opt => opt.value === selectedMinuteStep?.value)) {
            setSelectedMinuteStep(availableOptions[0]);
        }
    }, [interval, getAvailableOptions, selectedMinuteStep]);

    return (

        <div className="time-grid-selector" onMouseUp={handleMouseUp}>

            <ButtonBasic
                grey
                text='Очистить'
                onClick={clearAllSlots}
                style={{ marginLeft: 'auto' }}
                disabled={selectedDays.length === 0}
                size='32px'
            />

            <div className="time-grid-selector__content">
                <div className="time-grid-selector__controls-wrapper">
                    <TextPage
                        medium
                        color={'#94A3B8'}
                        text={<>Выбери дни недели и интервал и нажми <span style={{ color: '#1890FF', fontWeight: 600 }}>"Выбрать"</span></>}
                    />
                    <div className="time-grid-selector__controls">
                        <TextPage text="Дни недели:" style={{ fontSize: '12px', fontWeight: 600 }} />
                        <div className="time-grid-selector__day-selector">
                            {days.map((day, index) => (
                                <button
                                    key={day}
                                    className={`time-grid-selector__day-btn ${selectedDays.includes(index) ? 'active' : ''}`}
                                    onClick={() => toggleDay(index)}
                                >
                                    {day}
                                </button>
                            ))}
                        </div>

                        <DropDownSelector
                            placeholder='Интервал (мин)'
                            options_prop={getAvailableOptions()}
                            state={selectedMinuteStep}
                            setState={(e) => setSelectedMinuteStep(e)}
                            isClearable={false}
                        />

                        {/* < Checkbox
                    value={isCustom}
                    onChange={checked => setIsCustom(checked)}>
                    Ручная настройка
                </Checkbox> */}

                        <div className="time-grid-selector__action-buttons">
                            <ButtonBasic
                                blue
                                width='150px'
                                text='Выбрать'
                                onClick={fillAllSlots}
                                disabled={selectedDays.length === 0}
                                size='32px'
                            />
                        </div>

                        {child}
                    </div>
                </div>


                <div className="time-grid-selector__group-wrapper">
                    <TextPage
                        medium
                        color={'#94A3B8'}
                        text='Выбери период на карте слотов'
                    />
                    <div className="time-grid-selector__group">
                        <div className="time-grid-selector__grid-container">
                            <div></div>
                            {days.map((day, weekday) => (
                                <div
                                    key={day}
                                    className={`time-grid-selector__header-cell ${selectedDays.includes(weekday) ? 'active' : ''}`}
                                >
                                    {day}
                                </div>
                            ))}

                            {hours.flatMap(hour =>
                                minuteSteps().map(minute => (
                                    <React.Fragment key={`${hour}-${minute}`}>
                                        <div className="time-grid-selector__hour-cell">
                                            {String(hour).padStart(2, '0')}:{String(minute).padStart(2, '0')}
                                        </div>
                                        {days.map((day, weekday) => {
                                            const isSelected = isSlotSelected(weekday, hour, minute);
                                            return (
                                                <div
                                                    key={`${weekday}-${hour}-${minute}`}
                                                    onMouseDown={() => handleMouseDown(weekday, hour, minute)}
                                                    onMouseEnter={() => handleMouseEnter(weekday, hour, minute)}
                                                    className={`time-grid-selector__grid-cell 
                                                ${isSelected ? 'time-grid-selector__grid-cell_selected' : ''}`}
                                                    title={`${day} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`}
                                                ></div>
                                            );
                                        })}
                                    </React.Fragment>
                                ))
                            )}
                        </div>

                        <div className='time-grid-selector__selected-list-container'>
                            {selectedSlots.length > 0 && (
                                <>
                                    <TextPage
                                        style={{ color: '#51CA64', fontWeight: 600 }}
                                        text='Выбранные слоты:'
                                    />
                                    <div className='time-grid-selector__selected-list'>
                                        {selectedSlots.slice(0, 50).map((slot, index) => (
                                            <TextPage
                                                style={{ fontSize: '12px', fontWeight: 600 }}
                                                key={index}
                                                text={formatSlot(slot)}
                                            />
                                        ))}
                                        {selectedSlots.length > 50 && (
                                            <TextPage
                                                style={{ fontSize: '12px', fontWeight: 600 }}
                                                text={`...и еще ${selectedSlots.length - 50} слотов`}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { TimeGridSelector };